import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import { useForm } from "react-hook-form";
import { useAuth } from "./../util/auth";
import { ReCaptchaExecute } from "./ReCaptcha";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius * 1.5,
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.12)"
          : "rgba(0, 0, 0, 0.03)",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 2,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
    },
    "& .MuiInputLabel-outlined": {
      color:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.7)"
          : "rgba(0, 0, 0, 0.6)",
    },
    "& .MuiOutlinedInput-input": {
      color:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(0, 0, 0, 0.87)",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(16px, 14px) scale(1)",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 16px",
    },
  },
  submitButton: {
    borderRadius: theme.shape.borderRadius * 1.5,
    padding: "12px 24px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    boxShadow:
      "0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)",
    transition: theme.transitions.create([
      "background-color",
      "box-shadow",
      "transform",
    ]),
    "&:hover": {
      boxShadow:
        "0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(1px)",
    },
  },
  checkboxLabel: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
      color:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.7)"
          : theme.palette.text.secondary,
    },
  },
  checkbox: {
    padding: theme.spacing(1),
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
  },
}));

function AuthForm(props) {
  const classes = useStyles();
  const auth = useAuth();
  const [pending, setPending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaExecuted, setCaptchaExecuted] = useState(false);
  const { handleSubmit, register, errors, getValues } = useForm();

  const submitHandlersByType = {
    signin: ({ email, pass, marketingConsent }) => {
      // Only check for captcha token if execution has happened and failed
      // This makes the validation more lenient
      console.log("Captcha state:", { captchaToken, captchaExecuted });

      if (
        captchaExecuted &&
        !captchaToken &&
        ["signin", "signup"].includes(props.type)
      ) {
        console.error("reCAPTCHA verification failed");
        return Promise.reject(
          new Error("reCAPTCHA verification failed. Please try again.")
        );
      }

      return auth.signin(email, pass, marketingConsent).then((user) => {
        // Call auth complete handler
        props.onAuth(user);
      });
    },
    signup: ({ email, pass, marketingConsent }) => {
      // Only check for captcha token if execution has happened and failed
      // This makes the validation more lenient
      console.log("Captcha state:", { captchaToken, captchaExecuted });

      if (
        captchaExecuted &&
        !captchaToken &&
        ["signin", "signup"].includes(props.type)
      ) {
        console.error("reCAPTCHA verification failed");
        return Promise.reject(
          new Error("reCAPTCHA verification failed. Please try again.")
        );
      }

      return auth.signup(email, pass, marketingConsent).then((user) => {
        // Call auth complete handler
        props.onAuth(user);
      });
    },
    forgotpass: ({ email }) => {
      return auth.sendPasswordResetEmail(email).then(() => {
        setPending(false);
        // Show success alert message
        props.onFormAlert({
          type: "success",
          message: "Password reset email sent",
        });
      });
    },
    changepass: ({ pass }) => {
      return auth.confirmPasswordReset(pass).then(() => {
        setPending(false);
        // Show success alert message
        props.onFormAlert({
          type: "success",
          message: "Your password has been changed",
        });
      });
    },
  };

  // Handle captcha verification
  const handleCaptchaVerify = (token) => {
    console.log(
      "reCAPTCHA token received:",
      token ? "Valid token" : "No token"
    );
    setCaptchaToken(token);
    setCaptchaExecuted(true);
  };

  // Handle form submission
  const onSubmit = ({ email, pass, marketingConsent }) => {
    // Show pending indicator
    setPending(true);

    // Call submit handler for auth type
    submitHandlersByType[props.type]({
      email,
      pass,
      marketingConsent,
    }).catch((error) => {
      setPending(false);
      // Show error alert message
      props.onFormAlert({
        type: "error",
        message: error.message,
      });
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <Grid container={true} spacing={2}>
        {["signup", "signin", "forgotpass"].includes(props.type) && (
          <Grid item={true} xs={12}>
            <TextField
              className={classes.textField}
              variant="outlined"
              type="email"
              label="Email"
              name="email"
              placeholder="user@example.com"
              error={errors.email ? true : false}
              helperText={errors.email && errors.email.message}
              fullWidth={true}
              inputRef={register({
                required: "Please enter your email",
              })}
            />
          </Grid>
        )}

        {["signup", "signin", "changepass"].includes(props.type) && (
          <Grid item={true} xs={12}>
            <TextField
              className={classes.textField}
              variant="outlined"
              type="password"
              label="Password"
              name="pass"
              error={errors.pass ? true : false}
              helperText={errors.pass && errors.pass.message}
              fullWidth={true}
              inputRef={register({
                required: "Please enter a password",
              })}
            />
          </Grid>
        )}

        {["signup", "changepass"].includes(props.type) && (
          <Grid item={true} xs={12}>
            <TextField
              className={classes.textField}
              variant="outlined"
              type="password"
              label="Confirm Password"
              name="confirmPass"
              error={errors.confirmPass ? true : false}
              helperText={errors.confirmPass && errors.confirmPass.message}
              fullWidth={true}
              inputRef={register({
                required: "Please enter your password again",
                validate: (value) => {
                  if (value === getValues().pass) {
                    return true;
                  } else {
                    return "This doesn't match your password";
                  }
                },
              })}
            />
          </Grid>
        )}

        {["signup", "signin"].includes(props.type) && (
          <Grid item={true} xs={12}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  className={classes.checkbox}
                  name="marketingConsent"
                  color="primary"
                  defaultChecked={true}
                  inputRef={register()}
                  onChange={(e) => {
                    if (props.onMarketingConsentChange) {
                      props.onMarketingConsentChange(e.target.checked);
                    }
                  }}
                />
              }
              label="I agree to receive marketing communications about product updates and promotions."
            />
          </Grid>
        )}

        {/* Add invisible reCAPTCHA for signin and signup forms */}
        {["signup", "signin"].includes(props.type) && (
          <ReCaptchaExecute onVerify={handleCaptchaVerify} />
        )}

        <Grid item={true} xs={12}>
          <Button
            className={classes.submitButton}
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={pending}
            fullWidth={true}
          >
            {!pending && <span>{props.buttonAction}</span>}

            {pending && <CircularProgress size={28} />}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default AuthForm;
